import { useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Breadcrumbs,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Container,
  Link,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography
} from '@mui/material'
import { Bolt, Check, Hvac, Power, Thermostat } from '@mui/icons-material'
import PageHeader from '../components/PageHeader'
import { AssetType } from '../enums/AssetType'
import { usePolicy } from '../hooks'

type InstallOption = 'device' | 'heater' | 'generator' | 'ruuvitag'

export default function InstallPage() {
  /**
   * The translate function.
   */
  const { t } = useTranslation('installation')

  /**
   * The policy state.
   */
  const { canInstallHeater, canInstallGenerator } = usePolicy()

  /**
   * The navigate function.
   */
  const navigate = useNavigate()

  /**
   * The selected option.
   */
  const [selected, setSelected] = useState<InstallOption>()

  /**
   * The query string.
   */
  const { search } = useLocation()

  /**
   * Check if the option is selected.
   */
  function isSelected(option: InstallOption) {
    return selected === option
  }

  /**
   * Handle the "next" action.
   */
  function handleNext() {
    if (selected === 'device') {
      navigate(`/install/device${search}`)
    } else if (selected === 'heater') {
      navigate(`/install/heater${search}`)
    } else if (selected === 'generator') {
      navigate(`/install/generator${search}`)
    } else if (selected === 'ruuvitag') {
      navigate(`/install/ruuvi-tag${search}`)
    }
  }

  /**
   * Handle the "cancel" action.
   */
  function handleCancel() {
    navigate('/')
  }

  return (
    <Container maxWidth={false}>
      <PageHeader
        title={t('installAsset')}
        breadcrumbs={
          <Breadcrumbs>
            <Link component={NavLink} to="/">
              {t('frontpage')}
            </Link>
            <Typography>{t('installation')}</Typography>
          </Breadcrumbs>
        }
      />

      <Container maxWidth="md">
        <Card>
          <CardHeader title={t('selectInstallableAsset')} />
          <CardContent sx={{ pt: 0 }}>
            <List>
              <ListItemButton
                selected={isSelected(AssetType.DEVICE)}
                color="success"
                onClick={() => setSelected('device')}
              >
                <ListItemIcon sx={{ minWidth: 42 }}>
                  {isSelected(AssetType.DEVICE) ? <Check color="success" /> : <Bolt />}
                </ListItemIcon>
                <ListItemText
                  primary={t('asset:spineDevice')}
                  secondary={t('powerSupply')}
                  primaryTypographyProps={{
                    sx: { order: '0!important' }
                  }}
                  secondaryTypographyProps={{
                    sx: { order: '1!important' }
                  }}
                />
              </ListItemButton>

              {canInstallHeater() && (
                <ListItemButton
                  selected={isSelected('heater')}
                  onClick={() => setSelected('heater')}
                >
                  <ListItemIcon sx={{ minWidth: 42 }}>
                    {isSelected('heater') ? <Check color="success" /> : <Hvac />}
                  </ListItemIcon>
                  <ListItemText
                    primary={t('valve')}
                    secondary={t('heater')}
                    primaryTypographyProps={{
                      sx: { order: '0!important' }
                    }}
                    secondaryTypographyProps={{
                      sx: { order: '1!important' }
                    }}
                  />
                </ListItemButton>
              )}

              {canInstallGenerator() && (
                <ListItemButton
                  selected={isSelected('generator')}
                  color="primary"
                  onClick={() => setSelected('generator')}
                >
                  <ListItemIcon sx={{ minWidth: 42 }}>
                    {isSelected('generator') ? <Check color="success" /> : <Power />}
                  </ListItemIcon>
                  <ListItemText
                    primary={t('spineLoad')}
                    secondary={t('spineLoadTooltip')}
                    primaryTypographyProps={{
                      sx: { order: '0!important' }
                    }}
                    secondaryTypographyProps={{
                      sx: { order: '1!important' }
                    }}
                  />
                </ListItemButton>
              )}

              <ListItemButton
                selected={isSelected('ruuvitag')}
                color="primary"
                onClick={() => setSelected('ruuvitag')}
              >
                <ListItemIcon sx={{ minWidth: 42 }}>
                  {isSelected('ruuvitag') ? <Check color="success" /> : <Thermostat />}
                </ListItemIcon>
                <ListItemText
                  primary={t('ruuviTag')}
                  secondary={t('conditionMonitoring')}
                  primaryTypographyProps={{
                    sx: { order: '0!important' }
                  }}
                  secondaryTypographyProps={{
                    sx: { order: '1!important' }
                  }}
                />
              </ListItemButton>
            </List>
          </CardContent>

          <CardActions sx={{ justifyContent: 'flex-end' }}>
            <Button variant="text" color="inherit" onClick={handleCancel}>
              {t('cancel')}
            </Button>

            <Button
              variant="contained"
              color="primary"
              disabled={selected === undefined}
              onClick={handleNext}
            >
              {t('next')}
            </Button>
          </CardActions>
        </Card>
      </Container>
    </Container>
  )
}
